import type { CSSProperties } from 'react'
import { getNlsString } from '../../nls/nlsRuntime'
import { useShortLoad } from './useShortLoad'
import { SectionLoader } from './SectionLoader'

export interface LoaderProps {
  /** Primary dimension in pixels (height for wide variants, side for square). */
  size?: number
  /** Optional caption rendered below the animation. */
  label?: string
  /**
   * Preferred aria-label. Falls back to `label` then a generic "Loading".
   * Set explicitly for screen readers when the visible label is decorative.
   */
  ariaLabel?: string
  className?: string
  style?: CSSProperties
}

/**
 * Unified section loading indicator. Renders SectionLoader inside .appLoader__stage.
 */
export const Loader = ({
  size = 160,
  label,
  ariaLabel,
  className,
  style,
}: LoaderProps) => {
  const visible = useShortLoad(true)

  const rootStyle = {
    ...style,
    '--loader-size': `${size}px`,
    ...(visible ? {} : { visibility: 'hidden' }),
  } as CSSProperties

  return (
    <div
      className={`appLoader${className ? ` ${className}` : ''}`}
      style={rootStyle}
      role="status"
      aria-live="polite"
      aria-label={ariaLabel ?? label ?? getNlsString('NLSCommonStatusLoading')}
      data-short-load={visible ? undefined : 'pending'}
    >
      <div className="appLoader__stage" aria-hidden="true">
        <SectionLoader />
      </div>
      {label && <span className="appLoader__label">{label}</span>}
    </div>
  )
}
